import { useMemo } from "react";

import { sortBy } from "lodash";
import moment from "moment";

import {
  classes,
  Page,
  Table,
  type TableColumn,
  type TableFilter,
  Text,
} from "@alpacahq/ui/old";

import { useGetNonIdentityAccountDocuments } from "~/v2/api/hooks";
import {
  getNonIdentityAccountDocumentURL,
  type NonIdentityAccountDocument,
} from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";

const isETC = (clearingBroker?: string) =>
  clearingBroker === "ETC" ||
  clearingBroker === "VELOX" ||
  clearingBroker === "ALPACA_APCA";

const Documents = () => {
  const { currentAccount, product } = useAccountContext({ liveOnly: true });

  const { data: documents } = useGetNonIdentityAccountDocuments({
    accountId: currentAccount?.id,
    options: { enabled: product === "live" },
  });

  const rows = useMemo(
    () =>
      sortBy(documents ?? [], (doc) =>
        moment(doc.date, "YYYY-MM-DD").toDate().getTime(),
      ).reverse(),
    [documents],
  );

  const clearingBroker = currentAccount?.clearing_broker || "APEX";

  const columns = useMemo<TableColumn<NonIdentityAccountDocument>[]>(
    () => [
      { name: "Date", key: "date" },
      { name: "Document Type", key: "type" },
      {
        name: "Link",
        key: "link",
        cellRenderer: ({ id }) => (
          // todo: link component here
          <Text
            className={classes(
              "cursor-pointer text-blue-600 underline",
              !isETC(currentAccount?.clearing_broker) && "text-gray-500",
            )}
            onClickCapture={() =>
              isETC(currentAccount?.clearing_broker) &&
              !!currentAccount?.id &&
              getNonIdentityAccountDocumentURL(currentAccount?.id, id).then(
                (doc) => window.open(doc.url),
              )
            }
          >
            Download
          </Text>
        ),
      },
    ],
    [currentAccount?.id, currentAccount?.clearing_broker],
  );

  const documentTypes = useMemo(() => {
    const types = new Set<string>();
    rows.forEach((row) => {
      if (row.type) {
        types.add(row.type);
      }
    });
    return Array.from(types);
  }, [rows]);

  const filters: TableFilter<NonIdentityAccountDocument>[] = useMemo(
    () => [
      {
        key: "typeFilter",
        label: "Type",
        options: documentTypes.map((type) => ({
          label: type
            .replaceAll("_", " ")
            .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
            .replace("Json", "JSON"),
          value: type,
        })),
        filterFn: (row, filterValue) =>
          (Array.isArray(filterValue) ? filterValue : [filterValue]).some(
            (value) => value === row.type,
          ),
      },
    ],
    [documentTypes],
  );

  return (
    <Page
      title="Documents"
      subtitle="View account statements and trade confirmations."
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
    >
      <div className="space-y-12">
        <Table
          maxHeight={450}
          rows={rows}
          filters={filters}
          columns={columns}
          paginationEnabled
          searchEnabled
          pageSize={10}
        />
        {isETC(clearingBroker) && (
          <Text className="text-gray-500">
            The information and data contained in the .csv file(s) are from
            sources considered reliable but their accuracy and completeness is
            not guaranteed. This report has been prepared for informational
            purposes only and does not represent an official record of your
            account. Your account statement and Form 1099(s) are the official
            records of your account and should be referred to for any
            discrepancies.
          </Text>
        )}
      </div>
    </Page>
  );
};

export default Documents;
